import {Box, HBox} from '@buncha/components/core/view';
import {ClientType, OrderItemStatus} from '@buncha/common/types/common';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import {
  NSCItemScannerAction,
  useNSCItemScannerContext,
  usePickNSCItem,
} from '@buncha/itemDetails/hooks/NSCItemScanner';
import React, {useCallback} from 'react';
import {SafeAreaView, StyleSheet} from 'react-native';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import BarcodeList from '../BarcodeList';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import RetryUpload from '@buncha/components/core/icon/RetryUpload';
import {Touchable} from '@buncha/components/core/touchable';
import {isWeb} from '@buncha/utils/common';
import {navigation} from '@buncha/config/navigation';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface Props {
  setScannerActive: (val: boolean) => void;
  scannerActive: boolean;
  removeLastScannedBarcode: () => void;
  setTriggerAddAnother?: React.Dispatch<React.SetStateAction<boolean>>;
}

const BottomButtons = (props: Props) => {
  const {
    setScannerActive,
    scannerActive,
    removeLastScannedBarcode,
    setTriggerAddAnother,
  } = props;
  const theme = useColorThemeType('main');
  const {state, dispatch} = useNSCItemScannerContext();
  const routeParams: any = useRoute()?.params;
  const {button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const catalogItemId = routeParams?.catalogItemId;
  const orderItemId =
    state.buyers?.[0]?.orderItemId ?? routeParams?.orderItemId;
  const runId = routeParams?.runId;
  const returnPage = routeParams?.returnPage;

  const barcodes = state.barcodes;

  const nextBuyer = state.buyers?.[1];

  const {pickNSCItem, pickNSCItemLoading} = usePickNSCItem(dispatch);

  const addAnother = useCallback(() => {
    setScannerActive(true);
    if (setTriggerAddAnother) setTriggerAddAnother(prev => !prev);

    Analytics.eventWithProps('Add more barcodes', {
      type: EventType.cta,
      runId: runId,
      client: ClientType.meijer,
    });
  }, [runId, setScannerActive, setTriggerAddAnother]);

  const removeBarcode = useCallback(
    (barcode: string) => {
      dispatch?.(NSCItemScannerAction.removeBarcode(barcode));
    },
    [dispatch],
  );

  const packItem = useCallback(async () => {
    await pickNSCItem(
      orderItemId,
      runId,
      catalogItemId,
      barcodes,
      OrderItemStatus.packed,
    );
    return navigation.goBack();
  }, [barcodes, catalogItemId, orderItemId, pickNSCItem, runId]);

  const addItem = useCallback(async () => {
    if (
      returnPage === B2BScreens.B2BCheckout ||
      returnPage === ShopperScreens.OrderPackingPage
    )
      return packItem();
    const leftCount = await pickNSCItem(
      orderItemId,
      runId,
      catalogItemId,
      barcodes,
    );
    if (leftCount === 0) {
      if (returnPage === ShopperScreens.OrderPackingPage)
        return navigation.navigate(HomeScreens.Shopper, {
          screen: ShopperScreens.OrderPackingPage,
          params: {runId: runId},
        });
      navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.BatchDetailsPage,
        params: {runId: runId},
      });
    } else {
      setScannerActive(true);
    }
  }, [
    barcodes,
    catalogItemId,
    orderItemId,
    packItem,
    pickNSCItem,
    returnPage,
    runId,
    setScannerActive,
  ]);

  return (
    <Box style={styles.mainContainer}>
      <If condition={!scannerActive || isWeb()}>
        <If condition={!isWeb()}>
          <Touchable style={styles.retry} onPress={removeLastScannedBarcode}>
            <RetryUpload
              width={scaleFonts(40)}
              height={scaleFonts(40)}
              viewBox="0 -1 16 16"
              fill={theme?.text.blue[300]}
            />
          </Touchable>
        </If>
        <BarcodeList barcodes={barcodes} removeBarcode={removeBarcode} />
        <HBox
          style={[
            styles.buttonsContainer,
            {
              backgroundColor: theme?.background?.white?.[100],
            },
          ]}>
          <PButton
            title={button?.addAnother}
            style={styles.bottomButton}
            containerStyle={styles.buttonContainer}
            titleColor={theme?.text?.blue?.[100]}
            borderWidth={1}
            borderColor={theme?.background?.blue?.[100]}
            bgColor={theme?.background.white[100]}
            onPress={addAnother}
            disabled={pickNSCItemLoading}
          />
          <PButton
            title={nextBuyer ? button?.pickForNext : button?.proceed}
            subTitle={
              nextBuyer
                ? `${nextBuyer?.firstName} ${nextBuyer?.lastName}`
                : undefined
            }
            style={styles.bottomButton}
            containerStyle={styles.buttonContainer}
            titleColor={theme?.text?.white?.[100]}
            borderWidth={1}
            borderColor={theme?.background?.blue?.[100]}
            bgColor={theme?.background.blue[100]}
            onPress={addItem}
            disabled={pickNSCItemLoading || !state.barcodes?.length}
          />
        </HBox>
        <SafeAreaView
          style={{
            backgroundColor: theme?.background?.white?.[100],
          }}
        />
      </If>
    </Box>
  );
};

export default React.memo(BottomButtons);

const styles = StyleSheet.create({
  mainContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  bottomButton: {
    paddingHorizontal: 10,
    alignSelf: 'stretch',
    flex: 1,
  },
  buttonContainer: {
    flex: 1,
  },
  buttonsContainer: {
    padding: 10,
    gap: 10,
  },
  retry: {
    alignSelf: 'flex-end',
    borderRadius: 100,
    marginBottom: 10,
  },
  loadingComponent: {
    marginLeft: 5,
  },
});
